import React from 'react';
import { useTheme } from '../providers/ThemeProvider';
import { cx } from '@digitalisation/enlixy-comp';

export type RangePreset = '1d' | '3d' | '1w' | '2w' | 'custom' | 'live';

export const PRESETS: { id: RangePreset; label: string; days?: number }[] = [
  { id: 'live',   label: 'Live'    },
  { id: '1d',     label: '1 day',   days: 1  },
  { id: '3d',     label: '3 days',  days: 3  },
  { id: '1w',     label: '1 week',  days: 7  },
  { id: '2w',     label: '2 weeks', days: 14 },
  { id: 'custom', label: 'Custom'  },
];

/**
 * Compute the ISO-8601 start time for a preset.
 * @param referenceDate  The newest OPC-UA source timestamp in the stream.
 *                       When provided, "3 days" means 3 days before this date
 *                       (not 3 days before wall-clock time).
 */
export function presetToStartTime(preset: RangePreset, customDate?: string, referenceDate?: string | null): string | undefined {
  if (preset === 'live') return undefined;
  if (preset === 'custom' && customDate) return new Date(customDate).toISOString();
  const p = PRESETS.find(x => x.id === preset);
  if (p?.days) {
    const ref = referenceDate ? new Date(referenceDate) : new Date();
    ref.setDate(ref.getDate() - p.days);
    return ref.toISOString();
  }
  return undefined;
}

/**
 * Returns the minimum ms between stored history points for JetStream replay.
 * Scales with range so the chart never exceeds ~1 000 overview points per motor.
 *   live   → undefined (no history replay)
 *   1d     →  2 min  → ~720 pts
 *   3d     →  5 min  → ~864 pts
 *   1w     → 15 min  → ~672 pts
 *   2w     → 30 min  → ~672 pts
 *   custom → computed from duration
 */
export function presetToSampleIntervalMs(preset: RangePreset, customDate?: string): number | undefined {
  if (preset === 'live') return undefined;
  if (preset === '1d') return 2 * 60 * 1000;
  if (preset === '3d') return 5 * 60 * 1000;
  if (preset === '1w') return 15 * 60 * 1000;
  if (preset === '2w') return 30 * 60 * 1000;
  if (preset === 'custom' && customDate) {
    const ageMs = Date.now() - new Date(customDate).getTime();
    if (ageMs <= 1 * 86_400_000) return 2 * 60 * 1000;
    if (ageMs <= 3 * 86_400_000) return 5 * 60 * 1000;
    if (ageMs <= 7 * 86_400_000) return 15 * 60 * 1000;
    return 30 * 60 * 1000;
  }
  return undefined;
}

export interface DateRangeState {
  preset: RangePreset;
  customDate: string;
  /** ISO-8601 — oldest OPC-UA source timestamp in stream, shown in date picker min */
  oldestDate: string | null;
  /** ISO-8601 — newest OPC-UA source timestamp in stream, used as reference for preset ranges */
  newestDate: string | null;
  replaying: boolean;
  replayProgress: number;
  /** Whether live updates are currently frozen */
  paused: boolean;
  /** ISO-8601 timestamp at which the user paused; used to replay missed data on resume */
  pausedAt: string | null;
  /**
   * Computed once when the preset is selected — never recalculated on re-render.
   * Prevents infinite useEffect loops caused by new Date() drifting each render.
   */
  startTime: string | undefined;
}

export const initialDateRangeState: DateRangeState = {
  preset: 'live',
  customDate: '',
  oldestDate: null,
  newestDate: null,
  replaying: false,
  replayProgress: 0,
  paused: false,
  pausedAt: null,
  startTime: undefined,
};

interface DateRangeBarProps {
  state: DateRangeState;
  onChange: (preset: RangePreset, customDate: string) => void;
  onPauseToggle: () => void;
  onOldestDateAvailable?: (date: string) => void;
}

const DateRangeBar: React.FC<DateRangeBarProps> = ({ state, onChange, onPauseToggle }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;
  const { preset, customDate, oldestDate, replaying, replayProgress, paused } = state;
  const minDate = oldestDate ? oldestDate.slice(0, 16) : undefined;

  return (
    <div className={cx(
      'flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    )}>
      {PRESETS.map(p => (
        <button
          key={p.id}
          onClick={() => onChange(p.id, customDate)}
          className={cx(
            'px-3 py-1 rounded-lg text-xs font-medium transition-colors',
            preset === p.id
              ? 'bg-blue-600 text-white'
              : isDark
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          )}
        >
          {p.label}
        </button>
      ))}

      {preset === 'custom' && (
        <input
          type="datetime-local"
          value={customDate}
          min={minDate}
          max={new Date().toISOString().slice(0, 16)}
          onChange={e => {
            if (e.target.value) onChange('custom', e.target.value);
          }}
          className={cx(
            'text-xs rounded-lg border px-2 py-1',
            isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
          )}
        />
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Oldest available date hint */}
      {oldestDate && (
        <span className={cx('text-xs', isDark ? 'text-gray-500' : 'text-gray-400')}>
          Available from {new Date(oldestDate).toLocaleDateString()}
        </span>
      )}

      {/* Paused badge */}
      {paused && (
        <span className={cx(
          'text-xs px-2 py-0.5 rounded-full font-medium',
          isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700'
        )}>
          Paused — zoom freely
        </span>
      )}

      {/* Replay progress */}
      {replaying && !paused && (
        <div className="flex items-center gap-2">
          <div className={cx('w-24 h-1.5 rounded-full overflow-hidden', isDark ? 'bg-gray-700' : 'bg-gray-200')}>
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${replayProgress}%` }}
            />
          </div>
          <span className="text-xs text-blue-500">{replayProgress}%</span>
        </div>
      )}

      {/* Pause / Resume button */}
      <button
        onClick={onPauseToggle}
        title={paused ? 'Resume live updates (replays missed data)' : 'Pause live updates'}
        className={cx(
          'flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium border transition-colors',
          paused
            ? 'border-green-500 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
            : isDark
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
              : 'border-gray-300 text-gray-600 hover:bg-gray-100'
        )}
      >
        {paused ? (
          <>
            {/* Play / resume icon */}
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
            Resume
          </>
        ) : (
          <>
            {/* Pause icon */}
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
            Pause
          </>
        )}
      </button>
    </div>
  );
};

export default DateRangeBar;
